import React, { useEffect, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, batch } from 'react-redux';
import { Box } from '@mui/material';
import { SORT_MOST_POPULAR, SharedMarketplace, MarketplaceSpinner, MarketplaceEmptySearch } from '@alycecom/ui';

import { initMarketplace } from '../../store/settings/settings.actions';
import { getIsInit } from '../../store/settings/settings.selectors';
import {
  setPage,
  setLoading,
  resetFilters,
  resetProducts,
  setSorting,
  setSearch,
  clearAllChips,
  setCountryId,
} from '../../store/products/products.actions';
import { resetFilters as resetFiltersItems } from '../../store/filters/filters.actions';
import {
  getProducts,
  getIsLoading,
  getHasMore,
  getPagination,
  getSearch,
  getHasFilters,
  getIsMarketplaceEligibleForDeclaredData,
} from '../../store/products/products.selectors';
import ProductModule from '../../../ProductModule';
import { GiftProductShape, CountryShape } from '../../../../shapes';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';
import MarketplaceFilters from '../MarketplaceFilters/MarketplaceFilters';
import ProductItem from '../ProductItem/ProductItem';
import useResponsive from '../../../../hooks/useResponsive';
import spinner from '../../../../assets/images/spinner.svg';
import notFoundProduct from '../../assets/not-found-product.svg';
import TagsSelectionModal from '../TagsSelection/TagsSelectionModal';
import useGiftStatuses from '../../../RecipientModule/hooks/useGiftStatuses';
import useGoBack from '../../../../hooks/useGoBack';
import { loadSelectedTagsRequest, saveSelectedTagsRequest } from '../../store/tags/tags.actions';
import { getShouldSelectTags } from '../../store/tags/tags.selectors';
import { SORT_PRODUCT_TYPE } from '../../constants/sorting.constants';

const Marketplace = ({
  hashId,
  sentProduct,
  optionsData,
  isDonation,
  countryId,
  countries,
  isInternational,
  declaredData,
  hasCustomMarketplace,
  onAccept,
  canAccept,
  wrapperProps,
  children,
  onChangeCountry,
}) => {
  const dispatch = useDispatch();
  const trackEvent = useTrackRecipient();
  const { isMobile } = useResponsive();

  const [product, setProduct] = useState(undefined);
  const [isProductDetailsOpen, setIsProductDetailsOpen] = useState(false);

  const isLoading = useSelector(getIsLoading);
  const products = useSelector(getProducts);
  const isMarketplaceInit = useSelector(getIsInit);
  const pagination = useSelector(getPagination);
  const hasMore = useSelector(getHasMore);
  const search = useSelector(getSearch);
  const hasFilters = useSelector(getHasFilters);
  const isMarketplaceEligibleForDeclaredData = useSelector(getIsMarketplaceEligibleForDeclaredData);

  const defaultSorting = useMemo(() => (hasCustomMarketplace ? SORT_PRODUCT_TYPE : SORT_MOST_POPULAR), [
    hasCustomMarketplace,
  ]);

  const handleViewProductDetails = useCallback(
    viewedProduct => {
      setProduct(viewedProduct);
      setIsProductDetailsOpen(true);
      trackEvent('Product - Viewed', { product: viewedProduct });
    },
    [trackEvent],
  );

  const handleCloseViewProductDetails = useCallback(() => {
    setProduct(undefined);
    setIsProductDetailsOpen(false);
  }, []);

  const handleAcceptProduct = useCallback(() => {
    if (sentProduct.hasOptions) {
      handleViewProductDetails(sentProduct);
      return;
    }
    onAccept({ ...sentProduct, fromMarketplace: true });
    trackEvent('Gift invitation - Clicked accept button', { product: sentProduct });
  }, [handleViewProductDetails, trackEvent, sentProduct, onAccept]);

  const handleAcceptDetailsProduct = useCallback(
    (options = '', optionsValues) =>
      onAccept({ ...product, options, optionsData: optionsValues, fromMarketplace: true }),
    [product, onAccept],
  );

  const loadNextPage = useCallback(() => {
    if (!isLoading && hasMore) {
      dispatch(setPage(pagination.currentPage + 1));
    }
  }, [isLoading, pagination, dispatch, hasMore]);

  // Set isLoading for showing the loader before hash id is received
  useEffect(() => {
    dispatch(setLoading(true));
  }, [dispatch]);

  useEffect(() => {
    if (hashId) {
      dispatch(initMarketplace({ hashId, isDonation }));
    }
  }, [dispatch, hashId, isDonation]);

  useEffect(() => {
    if (isMarketplaceInit) {
      batch(() => {
        dispatch(resetFiltersItems());
        dispatch(clearAllChips());
        dispatch(setSorting({ id: defaultSorting.id, value: defaultSorting.value }));
        dispatch(resetFilters());
        dispatch(resetProducts());
        dispatch(setSearch(''));
        dispatch(setCountryId(countryId));
        dispatch(setPage(1));
      });
    }
  }, [dispatch, isMarketplaceInit, defaultSorting, countryId]);

  const [productsWrapperProps, setProductsWrapperProps] = useState({});
  const handleOnFiltersSticky = useCallback((cmp, isSticky) => {
    if (isSticky) {
      const { height } = cmp.current.getBoundingClientRect();
      setProductsWrapperProps({ mt: `${height + 40}px` });
    } else {
      setProductsWrapperProps({});
    }
  }, []);

  const isDeclaredDataEnabled = declaredData && !isDonation && !isInternational && isMarketplaceEligibleForDeclaredData;

  const isTagsModalOpen = useSelector(getShouldSelectTags);
  const handleSaveSelectedTags = useCallback(
    selectedTags => {
      dispatch(saveSelectedTagsRequest({ hashId, ...selectedTags }));
    },
    [dispatch, hashId],
  );
  const { previousStepUrl } = useGiftStatuses(true);
  const handleCancelSelectTags = useGoBack(previousStepUrl);

  useEffect(() => {
    if (isDeclaredDataEnabled) {
      dispatch(dispatch(loadSelectedTagsRequest({ hashId })));
    }
  }, [dispatch, hashId, isDeclaredDataEnabled]);

  const renderItem = useCallback(
    productItem => (
      <ProductItem
        key={productItem.id}
        product={productItem}
        isSentProduct={sentProduct && productItem.id === sentProduct.id}
        onAccept={handleAcceptProduct}
        onViewDetails={handleViewProductDetails}
      />
    ),
    [handleAcceptProduct, handleViewProductDetails, sentProduct],
  );

  const renderFakeItem = useCallback(() => {
    if (canAccept && sentProduct && sentProduct.countryId === countryId) {
      return (
        <ProductItem
          key={sentProduct.id}
          product={sentProduct}
          isSentProduct
          onAccept={handleAcceptProduct}
          onViewDetails={handleViewProductDetails}
        />
      );
    }
    return null;
  }, [handleAcceptProduct, handleViewProductDetails, canAccept, sentProduct, countryId]);

  const renderEmpty = useCallback(
    () => (
      <MarketplaceEmptySearch
        image={notFoundProduct}
        hasFilters={hasFilters}
        search={search}
        justifyContent="flex-start"
      />
    ),
    [search, hasFilters],
  );

  return (
    <>
      <Box width={1} display="flex" flexDirection="column" justifyContent="flex-start" {...wrapperProps}>
        <MarketplaceFilters
          isDonation={isDonation}
          isDeclaredDataEnabled={isDeclaredDataEnabled}
          hasCustomMarketplace={hasCustomMarketplace}
          countryId={countryId}
          countries={countries}
          onSticky={handleOnFiltersSticky}
          onChangeCountry={onChangeCountry}
        />
        <SharedMarketplace
          isLoading={isLoading}
          products={products}
          productsWrapperProps={productsWrapperProps}
          skeletonProps={{ isMobile }}
          loadNextPage={loadNextPage}
          hasMore={hasMore}
          loader={<MarketplaceSpinner image={spinner} key={0} />}
          renderItem={renderItem}
          renderFakeItem={renderFakeItem}
          renderEmpty={renderEmpty}
        />
        {children}
      </Box>
      <ProductModule
        isOpen={isProductDetailsOpen}
        hashId={hashId}
        productId={product && product.id}
        optionsData={optionsData}
        onClose={handleCloseViewProductDetails}
        onSubmit={handleAcceptDetailsProduct}
      />

      {isDeclaredDataEnabled && (
        <TagsSelectionModal
          isOpen={isTagsModalOpen}
          onSave={handleSaveSelectedTags}
          onCancel={handleCancelSelectTags}
          hashId={hashId}
        />
      )}
    </>
  );
};

Marketplace.propTypes = {
  hashId: PropTypes.string.isRequired,
  countryId: PropTypes.number,
  countries: PropTypes.arrayOf(CountryShape),
  isInternational: PropTypes.bool,
  declaredData: PropTypes.bool,
  hasCustomMarketplace: PropTypes.bool,
  sentProduct: GiftProductShape.isRequired,
  onAccept: PropTypes.func.isRequired,
  isDonation: PropTypes.bool,
  wrapperProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  children: PropTypes.node,
  canAccept: PropTypes.bool,
  onChangeCountry: PropTypes.func,
  optionsData: PropTypes.objectOf(PropTypes.string),
};

Marketplace.defaultProps = {
  isDonation: false,
  wrapperProps: {},
  countries: undefined,
  countryId: undefined,
  isInternational: false,
  declaredData: false,
  hasCustomMarketplace: false,
  children: undefined,
  canAccept: true,
  onChangeCountry: undefined,
  optionsData: undefined,
};

export default Marketplace;
